import { useEffect, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  isEntityWithGridMapBulkAutofit,
  isSelectedIdSF,
  isTableSelectionAtLimit,
  selectedTableRowsIdsSetSF,
  shouldDeferLiveMapMapRender,
  warnTableSelectionLimitReached,
} from "@ryde/shared";

import { usePageContext } from "../../contexts";
import { useLiveMapAutofitBulkGate } from "./use-live-map-autofit-bulk-gate";

/** Live map grid row: instant checkbox UI, deferred map refresh when many routes selected. */

export const useLiveMapGridRowSelection = (rideId: number) => {
  const { entity } = usePageContext();

  const isLiveMapEntity = entity === "rideMap";

  const isGridMapBulkAutofitEntity = isEntityWithGridMapBulkAutofit(entity);

  const isSelectedRecoil = useRecoilValue(isSelectedIdSF([entity, rideId]));

  const setIsSelectedRecoil = useSetRecoilState(
    isSelectedIdSF([entity, rideId]),
  );

  const selectedIdsSet = useRecoilValue(selectedTableRowsIdsSetSF(entity));

  const selectedCount = selectedIdsSet.size;

  const { runBulkSelectionDeferringMapRender } = useLiveMapAutofitBulkGate();

  const [optimisticSelected, setOptimisticSelected] = useState<boolean | null>(
    null,
  );

  const isSelected = optimisticSelected ?? isSelectedRecoil;

  useEffect(() => {
    setOptimisticSelected(null);
  }, [isSelectedRecoil]);

  const toggleRowSelection = useEvent((): void => {
    const nextSelected = !isSelectedRecoil;

    if (
      nextSelected &&
      isLiveMapEntity &&
      isTableSelectionAtLimit(Array.from(selectedIdsSet), entity)
    ) {
      warnTableSelectionLimitReached(entity);
      return;
    }

    const nextSelectedCount = nextSelected
      ? selectedCount + 1
      : selectedCount - 1;

    setOptimisticSelected(nextSelected);

    const applySelection = (): void => {
      setIsSelectedRecoil(nextSelected);
    };

    if (
      isLiveMapEntity &&
      isGridMapBulkAutofitEntity &&
      shouldDeferLiveMapMapRender({
        currentSelectedCount: selectedCount,

        nextSelectedCount,
      })
    ) {
      runBulkSelectionDeferringMapRender(applySelection);

      return;
    }

    applySelection();
  });

  return {
    isSelected,
    toggleRowSelection,
  };
};
